import { ConfigurableColumns } from '@/components/ConfigurableColumns';
import { NumberField, TextField, FunctionField } from 'react-admin';
import { Chip } from '@mui/material';
import { CopiableIdDataGridField } from '@/components/CopiableIdField';

const renderSeverityChip = (record: any) => {
  const severity = record.violationSeverity?.level || 'none';
  const conversionStatus = record.conversionStatus?.status;
  const isSubscribed = record.conversionStatus?.isSubscribed;
  const isLifelongTrial =
    record.trialMetrics?.limit === 0 ||
    record.trialMetrics?.limit === null ||
    record.accountInfo?.isLifelongTrial === true;
  const notesBeyondLimit = record.trialMetrics?.notesBeyondLimit || 0;

  // Don't show severity chip if:
  // 1. Subscribed or lifelong trial (not relevant)
  // 2. Trial completed but no notes beyond limit (severity only relevant when violations exist)
  if (
    isSubscribed ||
    conversionStatus === 'subscribed' ||
    isLifelongTrial ||
    (conversionStatus === 'trial-completed' && notesBeyondLimit === 0)
  ) {
    return null;
  }

  const colorMap: Record<
    string,
    | 'default'
    | 'primary'
    | 'secondary'
    | 'error'
    | 'info'
    | 'success'
    | 'warning'
  > = {
    none: 'default',
    acceptable: 'success',
    moderate: 'warning',
    critical: 'error',
  };

  return (
    <Chip
      label={severity}
      color={colorMap[severity] || 'default'}
      size="small"
    />
  );
};

const renderConversionStatus = (record: any) => {
  const { status, isSubscribed, firstSubscribedAt } =
    record.conversionStatus || {};

  // Color mapping for conversion status chips
  const statusColorMap: Record<
    string,
    | 'default'
    | 'primary'
    | 'secondary'
    | 'error'
    | 'info'
    | 'success'
    | 'warning'
  > = {
    'trial-completed': 'warning',
    trialing: 'info',
    subscribed: 'success',
    customer: 'success',
    cancelled: 'error',
    'billing-issues': 'warning',
    'pending-cancellation': 'warning',
    reactivated: 'success',
    'lifelong-trial': 'info',
    'free-subscription': 'info',
  };

  let label: string;
  let color:
    | 'default'
    | 'primary'
    | 'secondary'
    | 'error'
    | 'info'
    | 'success'
    | 'warning' = 'default';

  if (isSubscribed && firstSubscribedAt) {
    label = `Subscribed (${new Date(
      firstSubscribedAt,
    ).toLocaleDateString()})`;
    color = 'success';
  } else if (status) {
    label = status;
    color = statusColorMap[status] || 'default';
  } else {
    label = 'Unknown';
  }

  return <Chip label={label} color={color} size="small" />;
};

export const columns: ConfigurableColumns[] = [
  {
    label: 'Id',
    source: 'id',
    sortable: true,
    exportFn: (record: any) => record.id,
    GridComponent: CopiableIdDataGridField,
    gridComponentProps: {
      targetNavigationResource: 'therapists',
      referenceField: '_id',
      copyLink: true,
      filter: ['', undefined], // Empty filter to disable filter button
    },
  },
  {
    label: 'Name',
    source: 'firstName',
    GridComponent: FunctionField,
    sortable: true,
    gridComponentProps: {
      render: (record: any) =>
        `${record.firstName || ''} ${record.lastName || ''}`.trim() ||
        'N/A',
    },
  },
  {
    label: 'Email',
    source: 'email',
    GridComponent: TextField,
    sortable: true,
  },
  {
    label: 'Trial Limit',
    source: 'trialMetrics.limit',
    GridComponent: NumberField,
    sortable: true,
  },
  {
    label: 'Trial Usage',
    source: 'trialMetrics.usage',
    GridComponent: NumberField,
    sortable: true,
  },
  {
    label: 'Committed Notes',
    source: 'trialMetrics.committedNotes',
    GridComponent: NumberField,
    sortable: true,
  },
  {
    label: 'Notes Beyond Limit',
    source: 'trialMetrics.notesBeyondLimit',
    GridComponent: FunctionField,
    sortable: true,
    gridComponentProps: {
      render: (record: any) => {
        const value = record.trialMetrics?.notesBeyondLimit || 0;
        return (
          <span
            style={{
              color: value > 0 ? '#f44336' : 'inherit',
              fontWeight: value > 0 ? 'bold' : 'normal',
            }}
          >
            {value}
          </span>
        );
      },
    },
  },
  {
    label: 'Offline %',
    source: 'offlineAnalysis.offlinePercentage',
    GridComponent: FunctionField,
    sortable: true,
    gridComponentProps: {
      render: (record: any) =>
        record.offlineAnalysis?.offlinePercentage
          ? `${record.offlineAnalysis.offlinePercentage.toFixed(1)}%`
          : 'N/A',
    },
  },
  {
    label: 'Severity',
    source: 'violationSeverity.level',
    GridComponent: FunctionField,
    sortable: true,
    gridComponentProps: {
      render: renderSeverityChip,
    },
  },
  {
    label: 'Needs Investigation',
    source: 'violationSeverity.needsInvestigation',
    GridComponent: FunctionField,
    sortable: true,
    gridComponentProps: {
      render: (record: any) =>
        record.violationSeverity?.needsInvestigation ? 'Yes' : 'No',
    },
  },
  {
    label: 'Conversion Status',
    source: 'conversionStatus.status',
    GridComponent: FunctionField,
    sortable: true,
    gridComponentProps: {
      render: renderConversionStatus,
    },
  },
];
